import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Snackbar,
  Alert,
  Autocomplete,
  LinearProgress,
  IconButton,
  InputAdornment,
  Tooltip,
  Divider,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  Paid as PaidIcon,
  LocalShipping as LocalShippingIcon,
  HourglassEmpty as HourglassIcon,
  ShoppingCart as ShoppingCartIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Grain as GrainIcon,
  CreditCard as CreditCardIcon,
  EventNote as EventNoteIcon,
} from '@mui/icons-material';
import {
  useFeedProcurements,
  useMarkDelivered,
  useCancelProcurement,
  useFeedTypes,
  useProcureFeed,
  useSuppliers,
} from '../hooks/useFeed';
import type { FeedProcurement } from '../types';
import { useFeedBrands } from '../hooks/useOthers';

// ── helpers ────────────────────────────────────────────────────────────────────

function fmtDate(val: string) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const parseCost = (v: any) => parseFloat(v) || 0;

type StatusKey = 'all' | 'pending' | 'delivered' | 'cancelled';

const STATUS_FILTERS: { key: StatusKey; label: string; color: string }[] = [
  { key: 'all', label: 'All', color: '#ff9800' },
  { key: 'pending', label: 'Pending', color: '#ff9800' },
  { key: 'delivered', label: 'Delivered', color: '#4caf50' },
  { key: 'cancelled', label: 'Cancelled', color: '#f44336' },
];

// ── status chip ────────────────────────────────────────────────────────────────

function StatusChip({ status }: { status?: string }) {
  if (status === 'delivered')
    return (
      <Chip
        icon={
          <CheckCircleIcon sx={{ fontSize: '0.75rem !important', color: '#4caf50 !important' }} />
        }
        label="Delivered"
        size="small"
        sx={{
          height: 22,
          fontSize: '0.65rem',
          fontWeight: 700,
          bgcolor: 'rgba(76,175,80,0.12)',
          color: '#4caf50',
          border: '1px solid rgba(76,175,80,0.3)',
          '& .MuiChip-icon': { ml: '4px' },
        }}
      />
    );
  if (status === 'cancelled')
    return (
      <Chip
        icon={<CancelIcon sx={{ fontSize: '0.75rem !important', color: '#f44336 !important' }} />}
        label="Cancelled"
        size="small"
        sx={{
          height: 22,
          fontSize: '0.65rem',
          fontWeight: 700,
          bgcolor: 'rgba(244,67,54,0.12)',
          color: '#f44336',
          border: '1px solid rgba(244,67,54,0.3)',
          '& .MuiChip-icon': { ml: '4px' },
        }}
      />
    );
  return (
    <Chip
      icon={<HourglassIcon sx={{ fontSize: '0.75rem !important', color: '#ff9800 !important' }} />}
      label="Pending"
      size="small"
      sx={{
        height: 22,
        fontSize: '0.65rem',
        fontWeight: 700,
        bgcolor: 'rgba(255,152,0,0.12)',
        color: '#ff9800',
        border: '1px solid rgba(255,152,0,0.3)',
        '& .MuiChip-icon': { ml: '4px' },
      }}
    />
  );
}

// ── credit chip ────────────────────────────────────────────────────────────────

function CreditChip({ paymentDueDate }: { paymentDueDate?: string }) {
  const isOverdue = paymentDueDate && new Date(paymentDueDate) < new Date();
  return (
    <Chip
      icon={
        <CreditCardIcon
          sx={{
            fontSize: '0.7rem !important',
            color: `${isOverdue ? '#ff7043' : '#ce93d8'} !important`,
          }}
        />
      }
      label={isOverdue ? 'Overdue' : 'Credit'}
      size="small"
      sx={{
        height: 19,
        fontSize: '0.6rem',
        fontWeight: 700,
        bgcolor: isOverdue ? 'rgba(255,112,67,0.1)' : 'rgba(206,147,216,0.1)',
        color: isOverdue ? '#ff7043' : '#ce93d8',
        border: `1px solid ${isOverdue ? 'rgba(255,112,67,0.3)' : 'rgba(206,147,216,0.25)'}`,
        '& .MuiChip-icon': { ml: '4px' },
      }}
    />
  );
}

// ── main component ─────────────────────────────────────────────────────────────

const FeedProcurementsPage: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<StatusKey>('all');
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });

  const [deliverDialog, setDeliverDialog] = useState(false);
  const [procureDialog, setProcureDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState<FeedProcurement | null>(null);
  const [selectedProcurement, setSelectedProcurement] = useState<FeedProcurement | null>(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning',
  });
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const [deliverForm, setDeliverForm] = useState({
    quantity_delivered_kg: '',
    delivery_date: new Date().toISOString().split('T')[0],
  });

  const [procureForm, setProcureForm] = useState({
    feed_type_id: '',
    feed_brand_id: '',
    bags_purchased: '',
    cost_per_bag: '',
    procurement_date: new Date().toISOString().split('T')[0],
    delivery_date: '',
    notes: '',
    supplier_id: '',
    is_credit: false,
    payment_due_date: '',
  });

  const {
    data: procurementsData,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useFeedProcurements({ per_page: 1000 });

  const { data: feedTypes, isFetching: feedTypesFetching } = useFeedTypes();
  const { data: feedBrands, isFetching: feedBrandsFetching } = useFeedBrands();
  const { data: suppliersRaw } = useSuppliers({
    category_id: 1,
  });
  const suppliers: {
    id: number;
    name: string;
    phone?: string;
    categories?: { id: number; name: string }[];
  }[] = Array.isArray(suppliersRaw) ? suppliersRaw : ((suppliersRaw as any)?.data ?? []);
  const deliverMutation = useMarkDelivered();
  const cancelMutation = useCancelProcurement();
  const procureMutation = useProcureFeed();

  const allProcurements: FeedProcurement[] = useMemo(
    () => procurementsData?.data || [],
    [procurementsData]
  );

  const filtered = useMemo(() => {
    return allProcurements.filter((p) => {
      const matchStatus = statusFilter === 'all' || p.status === statusFilter;
      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        (p.procurement_number ?? '').toLowerCase().includes(q) ||
        (p.feed_type?.name ?? '').toLowerCase().includes(q) ||
        (p.feed_brand?.name ?? '').toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [allProcurements, statusFilter, search]);

  const countFor = (key: StatusKey) =>
    key === 'all' ? allProcurements.length : allProcurements.filter((p) => p.status === key).length;

  // Stats
  const totalBags = allProcurements.reduce((s, p) => s + parseCost(p.bags_purchased), 0);
  const totalCost = allProcurements.reduce((s, p) => s + parseCost(p.total_cost), 0);
  const pendingCount = allProcurements.filter((p) => p.status === 'pending').length;
  const deliveredCount = allProcurements.filter((p) => p.status === 'delivered').length;
  const creditCount = allProcurements.filter((p) => p.is_credit).length;
  const creditCost = allProcurements
    .filter((p) => p.is_credit && p.status !== 'cancelled')
    .reduce((s, p) => s + parseCost(p.total_cost), 0);

  // ── actions ─────────────────────────────────────────────────────────────────

  const handleDeliver = async () => {
    if (!selectedProcurement || !deliverForm.quantity_delivered_kg) return;
    try {
      await deliverMutation.mutateAsync({
        procurementId: selectedProcurement.id,
        data: {
          quantity_delivered_kg: parseFloat(deliverForm.quantity_delivered_kg),
          delivery_date: deliverForm.delivery_date,
        },
      });
      showSnackbar('Delivery recorded successfully');
      setDeliverDialog(false);
      setSelectedProcurement(null);
    } catch {
      showSnackbar('Failed to record delivery', 'error');
    }
  };

  const handleCancel = async () => {
    if (!cancelDialog) return;
    try {
      await cancelMutation.mutateAsync(cancelDialog.id);
      showSnackbar('Procurement cancelled');
      setCancelDialog(null);
    } catch {
      showSnackbar('Failed to cancel procurement', 'error');
    }
  };

  const resetProcureForm = () =>
    setProcureForm({
      feed_type_id: '',
      feed_brand_id: '',
      bags_purchased: '',
      cost_per_bag: '',
      procurement_date: new Date().toISOString().split('T')[0],
      delivery_date: '',
      notes: '',
      supplier_id: '',
      is_credit: false,
      payment_due_date: '',
    });

  const handleProcure = async () => {
    if (
      !procureForm.feed_type_id ||
      !procureForm.feed_brand_id ||
      !procureForm.bags_purchased ||
      !procureForm.cost_per_bag ||
      !procureForm.procurement_date
    ) {
      showSnackbar('Please fill all required fields', 'warning');
      return;
    }
    if (procureForm.is_credit && !procureForm.payment_due_date) {
      showSnackbar('Payment due date is required for credit purchases', 'warning');
      return;
    }
    try {
      await procureMutation.mutateAsync({
        feed_type_id: parseInt(procureForm.feed_type_id),
        feed_brand_id: parseInt(procureForm.feed_brand_id),
        bags_purchased: procureForm.bags_purchased,
        cost_per_bag: procureForm.cost_per_bag,
        procurement_date: procureForm.procurement_date,
        delivery_date: procureForm.delivery_date || undefined,
        notes: procureForm.notes || undefined,
        supplier_id: procureForm.supplier_id ? parseInt(procureForm.supplier_id) : undefined,
        is_credit: procureForm.is_credit,
        payment_due_date: procureForm.is_credit ? procureForm.payment_due_date : undefined,
      } as any);
      showSnackbar('Feed procurement recorded successfully');
      setProcureDialog(false);
      resetProcureForm();
    } catch {
      showSnackbar('Failed to record procurement', 'error');
    }
  };

  // ── columns ─────────────────────────────────────────────────────────────────

  const columns: GridColDef[] = [
    {
      field: 'procurement_number',
      headerName: 'Ref #',
      flex: 1,
      minWidth: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" gap={1.2} height="100%">
          <Box
            sx={{
              width: 6,
              height: 28,
              borderRadius: 1,
              flexShrink: 0,
              bgcolor:
                params.row.status === 'delivered'
                  ? '#4caf50'
                  : params.row.status === 'cancelled'
                    ? '#f44336'
                    : '#ff9800',
            }}
          />
          <Typography variant="body2" fontWeight={600} sx={{ color: '#fff', fontSize: '0.82rem' }}>
            {params.value || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'procurement_date',
      headerName: 'Date',
      flex: 0.85,
      minWidth: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
          {fmtDate(params.value)}
        </Typography>
      ),
    },
    {
      field: 'feed_type',
      headerName: 'Feed Type',
      flex: 1.1,
      minWidth: 130,
      valueGetter: (_: any, row: any) => row.feed_type?.name || '—',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: '#ffb74d', fontSize: '0.82rem' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'feed_brand',
      headerName: 'Brand',
      flex: 1,
      minWidth: 120,
      valueGetter: (_: any, row: any) => row.feed_brand?.name || '—',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.8rem' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'bags_purchased',
      headerName: 'Bags',
      flex: 0.65,
      minWidth: 80,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={700} sx={{ color: '#66bb6a', fontSize: '0.82rem' }}>
          {parseFloat(params.value).toFixed(0)}
        </Typography>
      ),
    },
    {
      field: 'kg_purchased',
      headerName: 'Weight',
      flex: 0.75,
      minWidth: 90,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
          {parseFloat(params.value).toFixed(0)} kg
        </Typography>
      ),
    },
    {
      field: 'total_cost',
      headerName: 'Total Cost',
      flex: 0.9,
      minWidth: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ color: '#42a5f5', fontSize: '0.82rem', fontFamily: 'monospace' }}
        >
          GH₵{' '}
          {parseCost(params.value).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      minWidth: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" gap={0.75} height="100%">
          <StatusChip status={params.value} />
          {params.row.is_credit && <CreditChip paymentDueDate={params.row.payment_due_date} />}
        </Box>
      ),
    },
    {
      field: 'supplier',
      headerName: 'Supplier',
      flex: 0.9,
      minWidth: 120,
      valueGetter: (_: any, row: any) => row.supplier?.name || '—',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      minWidth: 90,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" height="100%">
          {params.row.status === 'pending' && (
            <>
              <Tooltip title="Mark as delivered" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedProcurement(params.row);
                      setDeliverForm({
                        quantity_delivered_kg: params.row.kg_purchased?.toString() ?? '',
                        delivery_date: new Date().toISOString().split('T')[0],
                      });
                      setDeliverDialog(true);
                    }}
                    sx={{
                      color: 'rgba(76,175,80,0.7)',
                      '&:hover': { color: '#4caf50', bgcolor: 'rgba(76,175,80,0.1)' },
                    }}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Cancel" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setCancelDialog(params.row)}
                    sx={{
                      color: 'rgba(244,67,54,0.6)',
                      '&:hover': { color: '#f44336', bgcolor: 'rgba(244,67,54,0.1)' },
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  // ── error state ─────────────────────────────────────────────────────────────

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth={480}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 3,
              mx: 'auto',
              mb: 3,
              bgcolor: 'rgba(244,67,54,0.1)',
              border: '1px solid rgba(244,67,54,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningIcon sx={{ color: '#f44336', fontSize: 36 }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', mb: 1 }}>
            Failed to load feed procurements
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 3 }}>
            {error instanceof Error ? error.message : 'An unexpected error occurred.'}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => refetch()}
            sx={{
              borderColor: 'rgba(244,67,54,0.4)',
              color: '#f44336',
              borderRadius: 2,
              px: 4,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { borderColor: '#f44336', bgcolor: 'rgba(244,67,54,0.08)' },
            }}
          >
            Try again
          </Button>
        </Box>
      </Box>
    );
  }

  const grandTotalCost =
    parseFloat(procureForm.bags_purchased || '0') * parseFloat(procureForm.cost_per_bag || '0');

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {(isFetching || feedTypesFetching || feedBrandsFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 2,
            bgcolor: 'transparent',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #4caf50 0%, #81c784 50%, #4caf50 100%)',
            },
          }}
        />
      )}

      {/* ── Page Header ── */}
      <Box
        sx={{
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0e0e10 0%, #1a1a1d 60%, #0e0e10 100%)',
          border: '1px solid rgba(76,175,80,0.15)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -60,
            right: -60,
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(76,175,80,0.07) 0%, transparent 65%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -40,
            left: 80,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,152,0,0.04) 0%, transparent 65%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            p: { xs: 2.5, md: 3 },
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              flexShrink: 0,
              background:
                'linear-gradient(135deg, rgba(76,175,80,0.18) 0%, rgba(76,175,80,0.06) 100%)',
              border: '1px solid rgba(76,175,80,0.22)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <GrainIcon sx={{ color: '#4caf50', fontSize: 28 }} />
          </Box>
          <Box flex={1}>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                color: '#fff',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                fontSize: { xs: '1.5rem', md: '1.9rem' },
              }}
            >
              Feed Procurements
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.38)', mt: 0.4 }}>
              Track feed orders, bag deliveries and procurement costs
            </Typography>
          </Box>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setProcureDialog(true)}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              color: '#fff',
              borderRadius: 2,
              px: { xs: 2, md: 3 },
              py: 1.2,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.875rem',
              boxShadow: '0 4px 18px rgba(76,175,80,0.32)',
              flexShrink: 0,
              '&:hover': {
                background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)',
                boxShadow: '0 6px 22px rgba(76,175,80,0.42)',
              },
            }}
          >
            New Procurement
          </Button>
        </Box>
      </Box>

      {/* ── Stats Row ── */}
      <Box display="flex" gap={2} mb={4} flexWrap="wrap">
        {[
          {
            label: 'Total Orders',
            value: allProcurements.length,
            sub: `${deliveredCount} delivered`,
            color: '#4caf50',
            Icon: ShoppingCartIcon,
          },
          {
            label: 'Total Bags',
            value: totalBags.toLocaleString(undefined, { maximumFractionDigits: 0 }),
            sub: 'bags purchased',
            color: '#66bb6a',
            Icon: InventoryIcon,
          },
          {
            label: 'Total Spend',
            value: `GH₵ ${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            sub: 'procurement cost',
            color: '#42a5f5',
            Icon: PaidIcon,
          },
          {
            label: 'Pending Delivery',
            value: pendingCount,
            sub: 'awaiting delivery',
            color: '#ff9800',
            Icon: LocalShippingIcon,
          },
          {
            label: 'On Credit',
            value: creditCount,
            sub: `GH₵ ${creditCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} owed`,
            color: '#ce93d8',
            Icon: CreditCardIcon,
          },
        ].map(({ label, value, sub, color, Icon }) => (
          <Paper
            key={label}
            elevation={0}
            sx={{
              flex: 1,
              minWidth: 180,
              p: 2.5,
              background: '#111318',
              border: '1px solid rgba(255,255,255,0.05)',
              borderLeft: `3px solid ${color}`,
              borderRadius: 2.5,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'default',
              transition: 'transform 0.18s ease, box-shadow 0.18s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: `0 12px 36px rgba(0,0,0,0.55), 0 0 0 1px ${color}22`,
              },
            }}
          >
            <Icon
              sx={{
                position: 'absolute',
                right: 14,
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 56,
                color: `${color}0D`,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.35)',
                letterSpacing: '0.7px',
                fontSize: '0.62rem',
                textTransform: 'uppercase',
                display: 'block',
                mb: 0.75,
              }}
            >
              {label}
            </Typography>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{
                color,
                lineHeight: 1,
                mb: 0.5,
                fontSize:
                  typeof value === 'string' && value.startsWith('GH') ? '1.05rem' : '1.6rem',
              }}
            >
              {value}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.68rem' }}
            >
              {sub}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* ── Table ── */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid rgba(76,175,80,0.15)',
          borderRadius: 2.5,
          overflow: 'hidden',
          background: '#0e0e10',
        }}
      >
        {/* Table header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: 'linear-gradient(90deg, rgba(76,175,80,0.06) 0%, transparent 100%)',
            borderBottom: '1px solid rgba(76,175,80,0.1)',
          }}
        >
          <GrainIcon sx={{ color: '#4caf50', fontSize: '1rem' }} />
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ color: '#fff', letterSpacing: '0.3px' }}
          >
            Procurement Records
          </Typography>
          <Chip
            label={allProcurements.length}
            size="small"
            sx={{
              height: 19,
              fontSize: '0.62rem',
              fontWeight: 700,
              bgcolor: 'rgba(76,175,80,0.1)',
              color: '#4caf50',
              border: '1px solid rgba(76,175,80,0.2)',
            }}
          />
        </Box>

        {/* Filter + search bar */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            borderBottom: '1px solid rgba(255,255,255,0.04)',
          }}
        >
          <Box display="flex" gap={1} flexWrap="wrap" flex={1}>
            {STATUS_FILTERS.map(({ key, label, color }) => {
              const active = statusFilter === key;
              const count = countFor(key);
              return (
                <Box
                  key={key}
                  component="button"
                  onClick={() => setStatusFilter(key)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.6,
                    borderRadius: 20,
                    cursor: 'pointer',
                    border: `1px solid ${active ? color : 'rgba(255,255,255,0.1)'}`,
                    bgcolor: active ? `${color}18` : 'transparent',
                    transition: 'all 0.15s ease',
                    '&:hover': { borderColor: color, bgcolor: `${color}12` },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: active ? 800 : 600,
                      color: active ? color : 'rgba(255,255,255,0.5)',
                    }}
                  >
                    {label}
                  </Typography>
                  {count > 0 && (
                    <Box
                      sx={{
                        minWidth: 18,
                        height: 18,
                        borderRadius: 9,
                        px: 0.5,
                        bgcolor: active ? color : 'rgba(255,255,255,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.62rem',
                          fontWeight: 800,
                          color: active ? '#fff' : 'rgba(255,255,255,0.45)',
                        }}
                      >
                        {count}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
          <TextField
            size="small"
            placeholder="Search by ref, type or brand…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearch('')}
                      sx={{ color: 'rgba(255,255,255,0.3)' }}
                    >
                      <CloseIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              },
            }}
            sx={{
              width: 260,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.82rem',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(76,175,80,0.4)' },
                '&.Mui-focused fieldset': { borderColor: '#4caf50' },
              },
            }}
          />
        </Box>

        <DataGrid
          rows={filtered}
          columns={columns}
          rowCount={filtered.length}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 15, 25, 50]}
          disableRowSelectionOnClick
          loading={isLoading}
          rowHeight={52}
          sx={{
            border: 0,
            background: 'transparent',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(255,255,255,0.04)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
            '& .MuiDataGrid-columnHeaders': {
              background: 'rgba(76,175,80,0.05)',
              borderBottom: '1px solid rgba(76,175,80,0.12)',
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.68rem',
              letterSpacing: '0.7px',
              color: 'rgba(76,175,80,0.7)',
              textTransform: 'uppercase',
            },
            '& .MuiDataGrid-row:hover': { background: 'rgba(76,175,80,0.04)' },
            '& .MuiDataGrid-row:nth-of-type(even)': { background: 'rgba(255,255,255,0.012)' },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(76,175,80,0.1)',
              background: 'rgba(76,175,80,0.03)',
            },
            '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
              { color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' },
            '& .MuiTablePagination-select': { color: 'rgba(255,255,255,0.7)' },
            '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.4)' },
            '& .MuiIconButton-root.Mui-disabled': { color: 'rgba(255,255,255,0.15)' },
            '& .MuiDataGrid-virtualScroller': { minHeight: 200 },
          }}
        />
      </Paper>

      {/* ══════════════════════════════════════
          New Procurement Dialog
      ══════════════════════════════════════ */}
      <Dialog
        open={procureDialog}
        onClose={() => {
          setProcureDialog(false);
          resetProcureForm();
        }}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3.5,
              background: '#0e0e10',
              border: '1px solid rgba(76,175,80,0.2)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2.5,
            borderBottom: '1px solid rgba(76,175,80,0.12)',
            background: 'linear-gradient(135deg, rgba(76,175,80,0.06) 0%, transparent 100%)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              flexShrink: 0,
              bgcolor: 'rgba(76,175,80,0.12)',
              border: '1px solid rgba(76,175,80,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <GrainIcon sx={{ color: '#4caf50', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{ color: '#fff', letterSpacing: '-0.01em' }}
            >
              New Feed Procurement
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.38)' }}>
              Record a feed order from a supplier
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 3,
              maxHeight: '58vh',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.35)',
                  letterSpacing: '0.7px',
                  fontSize: '0.62rem',
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 1.5,
                }}
              >
                Feed Details
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Autocomplete
                  size="small"
                  options={feedTypes || []}
                  getOptionLabel={(o) => o.name}
                  value={
                    feedTypes?.find((t) => t.id.toString() === procureForm.feed_type_id) || null
                  }
                  onChange={(_, v) =>
                    setProcureForm({ ...procureForm, feed_type_id: v?.id.toString() || '' })
                  }
                  renderInput={(p) => (
                    <TextField
                      {...p}
                      label="Feed Type"
                      required
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  )}
                />
                <Autocomplete
                  size="small"
                  options={feedBrands || []}
                  getOptionLabel={(o) => o.name}
                  value={
                    feedBrands?.find((b) => b.id.toString() === procureForm.feed_brand_id) || null
                  }
                  onChange={(_, v) =>
                    setProcureForm({ ...procureForm, feed_brand_id: v?.id.toString() || '' })
                  }
                  renderInput={(p) => (
                    <TextField
                      {...p}
                      label="Brand"
                      required
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  )}
                />
              </Box>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.35)',
                  letterSpacing: '0.7px',
                  fontSize: '0.62rem',
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 1.5,
                }}
              >
                Quantity & Cost
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <TextField
                  label="Bags Purchased"
                  type="number"
                  size="small"
                  required
                  value={procureForm.bags_purchased}
                  onChange={(e) =>
                    setProcureForm({ ...procureForm, bags_purchased: e.target.value })
                  }
                  slotProps={{ htmlInput: { min: 0.1, step: 0.1 } }}
                  sx={{ flex: 1, minWidth: 130, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
                <TextField
                  label="Cost per Bag (GH₵)"
                  type="number"
                  size="small"
                  required
                  value={procureForm.cost_per_bag}
                  onChange={(e) => setProcureForm({ ...procureForm, cost_per_bag: e.target.value })}
                  slotProps={{ htmlInput: { min: 0.01, step: 0.01 } }}
                  sx={{ flex: 1, minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Box>
              {procureForm.bags_purchased && procureForm.cost_per_bag && (
                <Box display="flex" gap={2} mt={1.5}>
                  <Box
                    sx={{
                      flex: 1,
                      px: 2,
                      py: 1.2,
                      borderRadius: 1.5,
                      background: 'rgba(76,175,80,0.08)',
                      border: '1px solid rgba(76,175,80,0.2)',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.4)',
                        fontSize: '0.6rem',
                        letterSpacing: '0.4px',
                      }}
                    >
                      WEIGHT
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={800}
                      sx={{ color: '#4caf50', fontFamily: 'monospace' }}
                    >
                      {(parseFloat(procureForm.bags_purchased) * 50).toFixed(1)} kg
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      px: 2,
                      py: 1.2,
                      borderRadius: 1.5,
                      background: 'rgba(66,165,245,0.08)',
                      border: '1px solid rgba(66,165,245,0.2)',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.4)',
                        fontSize: '0.6rem',
                        letterSpacing: '0.4px',
                      }}
                    >
                      TOTAL COST
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={800}
                      sx={{ color: '#42a5f5', fontFamily: 'monospace' }}
                    >
                      GH₵{' '}
                      {grandTotalCost.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.35)',
                  letterSpacing: '0.7px',
                  fontSize: '0.62rem',
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 1.5,
                }}
              >
                Dates
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <TextField
                  label="Procurement Date"
                  type="date"
                  size="small"
                  required
                  value={procureForm.procurement_date}
                  onChange={(e) =>
                    setProcureForm({ ...procureForm, procurement_date: e.target.value })
                  }
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { max: new Date().toISOString().split('T')[0] },
                  }}
                  sx={{ flex: 1, minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
                <TextField
                  label="Delivery Date (optional)"
                  type="date"
                  size="small"
                  value={procureForm.delivery_date}
                  onChange={(e) =>
                    setProcureForm({ ...procureForm, delivery_date: e.target.value })
                  }
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { min: procureForm.procurement_date },
                  }}
                  sx={{ flex: 1, minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Box>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

            {/* Credit Purchase */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={procureForm.is_credit}
                    onChange={(e) =>
                      setProcureForm({
                        ...procureForm,
                        is_credit: e.target.checked,
                        payment_due_date: '',
                      })
                    }
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#ce93d8' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: '#ce93d8',
                      },
                    }}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <CreditCardIcon
                      sx={{
                        fontSize: 16,
                        color: procureForm.is_credit ? '#ce93d8' : 'rgba(255,255,255,0.35)',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: procureForm.is_credit ? '#ce93d8' : 'rgba(255,255,255,0.5)',
                        fontSize: '0.85rem',
                      }}
                    >
                      Credit Purchase
                    </Typography>
                  </Box>
                }
              />
              {procureForm.is_credit && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(206,147,216,0.06)',
                    border: '1px solid rgba(206,147,216,0.2)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Payment Due Date"
                    type="date"
                    size="small"
                    required
                    fullWidth
                    value={procureForm.payment_due_date}
                    onChange={(e) =>
                      setProcureForm({ ...procureForm, payment_due_date: e.target.value })
                    }
                    slotProps={{
                      inputLabel: { shrink: true },
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <EventNoteIcon sx={{ fontSize: 16, color: '#ce93d8' }} />
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '& fieldset': { borderColor: 'rgba(206,147,216,0.35)' },
                        '&:hover fieldset': { borderColor: '#ce93d8' },
                        '&.Mui-focused fieldset': { borderColor: '#ce93d8' },
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#ce93d8' },
                    }}
                  />
                  <Autocomplete
                    size="small"
                    options={suppliers.filter((s: any) => s.is_active !== false)}
                    getOptionLabel={(o) => o.name}
                    value={
                      suppliers.find((s) => s.id.toString() === procureForm.supplier_id) || null
                    }
                    onChange={(_, v) =>
                      setProcureForm({ ...procureForm, supplier_id: v?.id.toString() || '' })
                    }
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#fff' }}>
                            {option.name}
                          </Typography>
                          {option.categories && option.categories.length > 0 && (
                            <Typography
                              variant="caption"
                              sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.68rem' }}
                            >
                              {option.categories.map((c: any) => c.name).join(' · ')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    renderInput={(p) => (
                      <TextField
                        {...p}
                        label="Supplier (optional)"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            '& fieldset': { borderColor: 'rgba(206,147,216,0.25)' },
                            '&:hover fieldset': { borderColor: '#ce93d8' },
                            '&.Mui-focused fieldset': { borderColor: '#ce93d8' },
                          },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#ce93d8' },
                        }}
                      />
                    )}
                  />
                </Box>
              )}
            </Box>

            <TextField
              label="Notes (optional)"
              multiline
              rows={2}
              size="small"
              value={procureForm.notes}
              onChange={(e) => setProcureForm({ ...procureForm, notes: e.target.value })}
              slotProps={{ htmlInput: { maxLength: 1000 } }}
              helperText={`${procureForm.notes.length}/1000`}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{ px: 3, py: 2, gap: 1.5, borderTop: '1px solid rgba(255,255,255,0.04)' }}
        >
          <Button
            onClick={() => {
              setProcureDialog(false);
              resetProcureForm();
            }}
            disabled={procureMutation.isPending}
            sx={{
              color: 'rgba(255,255,255,0.45)',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProcure}
            variant="contained"
            disabled={procureMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              color: '#fff',
              borderRadius: 1.5,
              px: 3.5,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: '0 4px 14px rgba(76,175,80,0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)' },
              '&:disabled': { background: 'rgba(76,175,80,0.2)', color: 'rgba(255,255,255,0.3)' },
            }}
          >
            {procureMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Record Procurement'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════
          Mark as Delivered Dialog
      ══════════════════════════════════════ */}
      <Dialog
        open={deliverDialog}
        onClose={() => setDeliverDialog(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3.5,
              background: '#0e0e10',
              border: '1px solid rgba(76,175,80,0.2)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2.5,
            borderBottom: '1px solid rgba(76,175,80,0.12)',
            background: 'linear-gradient(135deg, rgba(76,175,80,0.06) 0%, transparent 100%)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              flexShrink: 0,
              bgcolor: 'rgba(76,175,80,0.12)',
              border: '1px solid rgba(76,175,80,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LocalShippingIcon sx={{ color: '#4caf50', fontSize: 22 }} />
          </Box>
          <Box flex={1}>
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{ color: '#fff', letterSpacing: '-0.01em' }}
            >
              Record Delivery
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.38)' }}>
              {selectedProcurement?.procurement_number}
            </Typography>
          </Box>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Quantity Delivered (kg)"
              type="number"
              size="small"
              required
              fullWidth
              value={deliverForm.quantity_delivered_kg}
              onChange={(e) =>
                setDeliverForm({ ...deliverForm, quantity_delivered_kg: e.target.value })
              }
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            <TextField
              label="Delivery Date"
              type="date"
              size="small"
              required
              fullWidth
              value={deliverForm.delivery_date}
              onChange={(e) => setDeliverForm({ ...deliverForm, delivery_date: e.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, gap: 1.5, borderTop: '1px solid rgba(255,255,255,0.04)' }}
        >
          <Button
            onClick={() => setDeliverDialog(false)}
            disabled={deliverMutation.isPending}
            sx={{
              color: 'rgba(255,255,255,0.45)',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeliver}
            variant="contained"
            disabled={deliverMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              color: '#fff',
              borderRadius: 1.5,
              px: 3.5,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: '0 4px 14px rgba(76,175,80,0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)' },
              '&:disabled': { background: 'rgba(76,175,80,0.2)', color: 'rgba(255,255,255,0.3)' },
            }}
          >
            {deliverMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Confirm Delivery'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════
          Cancel Confirmation Dialog
      ══════════════════════════════════════ */}
      <Dialog
        open={!!cancelDialog}
        onClose={() => setCancelDialog(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              border: '1px solid rgba(244,67,54,0.2)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              mx: 'auto',
              mb: 2,
              bgcolor: 'rgba(244,67,54,0.1)',
              border: '1px solid rgba(244,67,54,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningIcon sx={{ color: '#f44336', fontSize: 26 }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', mb: 0.5 }}>
            Cancel Procurement?
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mb: 2.5 }}>
            This will permanently cancel
          </Typography>
          <Paper
            elevation={0}
            sx={{
              py: 1.5,
              px: 2,
              mb: 3,
              background: 'rgba(244,67,54,0.07)',
              border: '1px solid rgba(244,67,54,0.2)',
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" fontWeight={700} sx={{ color: '#fff' }}>
              {cancelDialog?.procurement_number}
            </Typography>
          </Paper>
          <Box display="flex" gap={1.5} justifyContent="center">
            <Button
              onClick={() => setCancelDialog(null)}
              disabled={cancelMutation.isPending}
              sx={{
                color: 'rgba(255,255,255,0.5)',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                px: 3,
                border: '1px solid rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
              }}
            >
              Keep
            </Button>
            <Button
              onClick={handleCancel}
              variant="contained"
              disabled={cancelMutation.isPending}
              sx={{
                background: 'linear-gradient(135deg, #f44336 0%, #e53935 100%)',
                color: '#fff',
                borderRadius: 1.5,
                px: 3,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: '0 4px 14px rgba(244,67,54,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%)' },
                '&:disabled': { background: 'rgba(244,67,54,0.2)', color: 'rgba(255,255,255,0.3)' },
              }}
            >
              {cancelMutation.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                'Cancel Order'
              )}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeedProcurementsPage;
